import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ErrorMessage, TimeListItem } from '..';
import { Utils } from '../../utils';

import { useContextSelector } from '../../hooks';
import type { RentalType } from '../../types';

export interface ITimeInputDropdownProps {
  id: string;
  placeholder: string;
  placeholderIcon?: string;
  type: RentalType;
  onTimeSelect: (selectedTime: string, flag: RentalType) => void;
}

export const TimeInputDropdown = ({
  id,
  placeholder,
  placeholderIcon,
  type,
  onTimeSelect,
  ...props
}: ITimeInputDropdownProps) => {
  const {
    errors: { timeError },
    liferay: { antecedenceTime },
    search: { dates, differentAgencies, location, time },
    inputs,
    update,
  } = useContextSelector();
  const [inputTextValue, setInputTextValue] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const timeArray = useMemo(() => {
    const START_TIME = '00:00:00';
    const END_TIME = '23:30:00';

    if (location[type] && dates[type]) {
      const isInternational = Utils.generics.isInternationalAgency(location[type]);
      const isSelectedDateAfterCurrentDay = new Date(dates[type] as string) >= new Date();

      if (!isSelectedDateAfterCurrentDay) {
        return Utils.time.generateTimeArray(START_TIME, END_TIME).filter((currentTime) => {
          const antecedence = Utils.generics.getAntecedenceByInternationality(antecedenceTime, isInternational);
          return Utils.time.filterTimeByAntecedenceHours(currentTime, antecedence, new Date());
        });
      }

      const today = new Date();
      const todayMinutes = today.getMinutes();
      const tomorrowDate = new Date();
      tomorrowDate.setDate(today.getDate() + 1);
      tomorrowDate.setHours(0, 0, 0, 0);
      const isTomorrowDate = new Date(dates[type] as string).valueOf() === tomorrowDate.valueOf();

      if (isTomorrowDate) {
        // apply the remaining antecedence hours on the next day (tomorrow)
        const todayHours = 24 - new Date().getHours();
        tomorrowDate.setHours(0, todayMinutes, 0, 0);

        return Utils.time.generateTimeArray(START_TIME, END_TIME).filter((currentTime) => {
          const antecedence = Utils.generics.getAntecedenceByInternationality(antecedenceTime, isInternational);
          return Utils.time.filterTimeByAntecedenceHours(currentTime, antecedence - todayHours, tomorrowDate);
        });
      }

      return Utils.time.generateTimeArray(START_TIME, END_TIME);
    }

    return [];
  }, [dates, location, antecedenceTime, type]);

  useEffect(() => {
    if (!isDropdownOpen) {
      const input = inputRef.current as HTMLInputElement;
      input.blur();
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const isInputOpen = type === 'pickup' ? inputs.timePickup : inputs.timeDropoff;

    if (isInputOpen) {
      const input = inputRef.current as HTMLInputElement;
      setIsDropdownOpen(true);
      input.focus();
    }
  }, [inputs, type]);

  useEffect(() => {
    Utils.helpers.handleClosePopUp(dropdownRef, () => setIsDropdownOpen(false));
  }, []);

  useEffect(() => {
    // Persist input text according to context state
    if (time[type]) {
      setInputTextValue(time[type] as string);
    } else {
      setInputTextValue('');
    }
  }, [time[type]]);

  const handleInputFocus = () => {
    update.errors.reset();

    const hasEmptyLocation = Utils.error.validateInputs(
      [
        ['location', { [type]: location[type] }],
        ['dates', { [type]: dates[type] }],
      ],
      differentAgencies,
      update.errors,
    );

    if (!hasEmptyLocation) {
      setIsDropdownOpen(true);
    }
  };

  const handleItemSelect = (selectedTime: string) => {
    setInputTextValue(selectedTime);
    setIsDropdownOpen(false);
    onTimeSelect(selectedTime, type);
  };

  return (
    <section className="time-input-container" ref={dropdownRef} {...props}>
      <section className="time-input-field-wrapper">
        <input
          className={`time-input-field${timeError[type].active ? ' error' : ''}${isDropdownOpen ? ' focused' : ''}`}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          ref={inputRef}
          id={`time-input-field-${id}`}
          placeholder={placeholder}
          value={inputTextValue}
          onFocus={handleInputFocus}
          readOnly
        />
      </section>

      <section
        className={`time-input-dropdown-container${isDropdownOpen ? ' open' : ''}`}
        id={`time-input-container-${id}`}
      >
        <ul className="time-input-dropdown-list" id={`time-input-dropdown-${id}`}>
          {timeArray.map((timeItem, index) => (
            <TimeListItem
              key={`time-input-dropdown-list-item-${index + 1}`}
              text={timeItem}
              type={type}
              onSelect={handleItemSelect}
            />
          ))}
        </ul>
      </section>

      <ErrorMessage isActive={timeError[type].active} message={timeError[type].message} />
    </section>
  );
};
